import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { CollapseList, StatusIcon, TaskListItem, ToggleType } from '@wix/rules-panel-ui';
import React, { useMemo, useState } from 'react';
import { useA11yWizardAdapter } from '../../../../../hooks/use-a11y-wizard-adapter';
import { useGetRuleIdToLabelMap } from '../../../../../hooks/use-get-ruleId-to-label-map';
import { useGetPageRuleIds } from '../utils/use-get-page-rule-ids';
import { CollapseActionsProvider, ItemScopeProvider } from './context/collapse-actions-context';
import { useOpenNextItem } from './hooks/use-open-next-item';
import { RuleContentWithCollapse } from './rule-content-with-collapse';
import { useHandleUnsavedChangesOnToggle } from './utils/use-handle-unsaved-changes-on-toggle';
export const SpecificPageTaskList = /*#__PURE__*/React.memo(_ref => {
  let {
    pageId,
    ruleResults,
    topComponent,
    isLoading
  } = _ref;
  const {
    t
  } = useTranslation();
  const [openItems, setOpenItems] = useState(new Set());
  const ruleIds = ruleResults.map(result => result.ruleId);
  const ruleIdsForSkeleton = useGetPageRuleIds(pageId, ruleIds);
  const {
    pageLevelruleIdToLabelKeyMap
  } = useGetRuleIdToLabelMap();
  const {
    capabilities
  } = useA11yWizardAdapter();
  const compactPanelChrome = capabilities.compactPanelChrome;
  const handleSetOpenItems = useHandleUnsavedChangesOnToggle(openItems, setOpenItems);
  const ruleResultsForAutoExpand = useMemo(() => {
    return ruleResults.map(result => {
      return {
        id: result.ruleId,
        status: result.status
      };
    });
  }, [ruleResults]);
  const {
    handlePulseComplete
  } = useOpenNextItem({
    items: ruleResultsForAutoExpand,
    pageId,
    setOpenItems
  });
  const tasks = useMemo(() => {
    if (isLoading || !ruleResults || ruleResults.length === 0) {
      return ruleIdsForSkeleton == null ? void 0 : ruleIdsForSkeleton.map(ruleId => {
        return /*#__PURE__*/React.createElement(ItemScopeProvider, {
          id: ruleId,
          key: ruleId
        }, /*#__PURE__*/React.createElement(TaskListItem, {
          id: ruleId,
          isLoading: true,
          showBottomShadow: !compactPanelChrome,
          showTopShadow: !compactPanelChrome,
          title: t(pageLevelruleIdToLabelKeyMap[ruleId]),
          variant: "collapsible"
        }));
      });
    }
    return ruleResults.map((ruleResult, index, array) => {
      const isLast = index === array.length - 1;
      return /*#__PURE__*/React.createElement(ItemScopeProvider, {
        id: ruleResult.ruleId,
        key: ruleResult.ruleId
      }, /*#__PURE__*/React.createElement(TaskListItem, {
        id: ruleResult.ruleId,
        prefix: /*#__PURE__*/React.createElement(StatusIcon, {
          status: ruleResult.status,
          onComplete: handlePulseComplete
        }),
        showBottomShadow: !isLast && !compactPanelChrome,
        showTopShadow: !compactPanelChrome,
        title: ruleResult.label,
        variant: "collapsible",
        onToggle: (_id, isOpen) => {
          ruleResult.onRuleToggle == null || ruleResult.onRuleToggle(isOpen ? ToggleType.Expand : ToggleType.Collapse, index, true // initiatedByUser
          );
        }
      }, /*#__PURE__*/React.createElement(Box, {
        dataHook: "specific-page-task-list-item-content",
        padding: "SP3 SP6 SP6 SP6"
      }, /*#__PURE__*/React.createElement(RuleContentWithCollapse, {
        ruleResult: ruleResult
      }))));
    });
  }, [isLoading, ruleResults, ruleIdsForSkeleton, t, pageLevelruleIdToLabelKeyMap, compactPanelChrome, handlePulseComplete]);
  return /*#__PURE__*/React.createElement(CollapseActionsProvider, {
    setOpenItems: handleSetOpenItems
  }, /*#__PURE__*/React.createElement(CollapseList, {
    autoScroll: true,
    contentGap: 0,
    openItems: openItems,
    setOpenItems: handleSetOpenItems
  }, topComponent, tasks));
});
//# sourceMappingURL=specific-page-task-list.js.map